/**
 * Custom hook for managing measurements data
 * Handles data fetching, mutations, and computed values
 */

import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import type {
  MeasurementFormData,
  Meranie,
  ChartDataPoint,
  MeasurementSummary,
} from "../types";
import { prepareChartData, prepareSummary } from "../utils";

interface UseMeasurementsDataProps {
  selectedKlientId: number | null;
}

export function useMeasurementsData({
  selectedKlientId,
}: UseMeasurementsDataProps) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  // Queries
  const { data: klienti } = trpc.klient.list.useQuery();
  const { data: merania, isLoading } = trpc.meranie.getByKlient.useQuery(
    { klientId: selectedKlientId!, limit: 50 },
    { enabled: !!selectedKlientId }
  );
  const { data: latestMeranie } = trpc.meranie.getLatest.useQuery(
    { klientId: selectedKlientId! },
    { enabled: !!selectedKlientId }
  );

  // Mutations
  const createMutation = trpc.meranie.create.useMutation();
  const updateMutation = trpc.meranie.update.useMutation();
  const deleteMutation = trpc.meranie.delete.useMutation();

  // Computed values
  const filteredKlienti = klienti?.filter(k => k.odberMeranie) || [];
  const chartData: ChartDataPoint[] = merania ? prepareChartData(merania) : [];
  const summary: MeasurementSummary | null = prepareSummary(
    latestMeranie || null
  );

  // Mutation handlers
  const createMeasurement = async (data: MeasurementFormData) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success(t("measurements:messages.measurementCreated"));
      utils.meranie.getByKlient.invalidate();
      utils.meranie.getLatest.invalidate();
      return true;
    } catch (error) {
      toast.error(t("common:error"));
      return false;
    }
  };

  const updateMeasurement = async (id: number, data: MeasurementFormData) => {
    try {
      await updateMutation.mutateAsync({ id, data });
      toast.success(t("measurements:messages.measurementUpdated"));
      utils.meranie.getByKlient.invalidate();
      utils.meranie.getLatest.invalidate();
      return true;
    } catch (error) {
      toast.error(t("common:error"));
      return false;
    }
  };

  const deleteMeasurement = async (id: number) => {
    if (!confirm(t("measurements:messages.deleteConfirm"))) return false;

    try {
      await deleteMutation.mutateAsync({ id });
      toast.success(t("measurements:messages.measurementDeleted"));
      utils.meranie.getByKlient.invalidate();
      utils.meranie.getLatest.invalidate();
      return true;
    } catch (error) {
      toast.error(t("common:error"));
      return false;
    }
  };

  return {
    // Data
    klienti: filteredKlienti,
    merania: merania || [],
    latestMeranie,
    chartData,
    summary,
    isLoading,

    // Mutations
    createMeasurement,
    updateMeasurement,
    deleteMeasurement,
  };
}
